// edit-report-hunt-package.component.ts
import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  computed,
  ChangeDetectorRef,
} from '@angular/core'
import { RouterModule } from '@angular/router'

import {
  AccordionComponent,
  CodeBlockComponent,
  TabBarComponent,
  TabComponent,
  TagComponent,
  TypographyComponent,
} from '@hyperkit'

import {
  HuntViewService,
  PackageViewService,
  PackageEditorService,
  NavigationService,
  HydrationService,
  SessionService,
} from '@hunt/services'

import { getToolIcon } from '../../../../helpers/utils'
import { HuntEditorComponent } from '../../details/components/hunt-editor'
import { ReportPivotBlockComponent } from '../../hunt-final-report/report-pivot-block/report-pivot-block.component'
import { ReportTextBlockComponent } from '../../hunt-final-report/report-text-block/report-text-block.component'
import { ReportQueryHeaderComponent } from '../../report-query-header/report-query-header.component'

@Component({
  standalone: true,
  selector: 'edit-report-hunt-package',
  imports: [
    CommonModule,
    RouterModule,
    TypographyComponent,
    TagComponent,
    ReportQueryHeaderComponent,
    ReportTextBlockComponent,
    AccordionComponent,
    HuntEditorComponent,
    TabComponent,
    TabBarComponent,
    CodeBlockComponent,
    ReportPivotBlockComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './edit-report-hunt-package.component.html',
  styleUrls: ['edit-report-hunt-package.component.scss'],
})
export class EditReportHuntPackageComponent {
  public readonly huntView = inject(HuntViewService)
  public readonly packageView = inject(PackageViewService)
  private readonly packageEditor = inject(PackageEditorService)
  private readonly nav = inject(NavigationService)
  private readonly hydration = inject(HydrationService)
  private readonly cdr = inject(ChangeDetectorRef)
  readonly session = inject(SessionService)
  readonly loaded = computed(
    () => !this.session.loading() && this.session.activeHunt() !== null,
  )

  /** URL‐driven state */
  public readonly huntPackageId = this.nav.packageId
  public readonly subtab = this.hydration.query('subtab', 'overview')

  constructor() {
    // re-render on subtab change
    effect(() => {
      this.subtab()
      this.cdr.detectChanges()
    })
  }

  /** All the “report” environments for this package */
  public readonly envs = this.packageView.getAllReportEnvironments

  public readonly tabLabels = ['overview', 'queryNotes']

  setSubtab(tab: 'overview' | 'queryNotes'): void {
    this.nav.setQueryParam('subtab', tab)
  }

  /** Persist new threat description */
  public async onDescriptionChange(newDescription: string): Promise<void> {
    const pkgId = this.huntPackageId()
    if (!pkgId) return
    await this.packageEditor.patchPackage({ description: newDescription })
  }

  /** Persist new mitigation recommendations */
  public async onMitigationRecommendationsChange(
    newMitigationRecommendations: string,
  ): Promise<void> {
    const pkgId = this.huntPackageId()
    if (!pkgId) return
    await this.packageEditor.patchPackage({
      response_actions: {
        mitigation_recommendations: newMitigationRecommendations,
      },
    })
  }

  /** For the little logo beside each tool */
  public getToolIcon = getToolIcon
}
